import { Link } from "react-router-dom";
import ProfileDropdown from "./dropdown/ProfileDropdown";
import useModal from "@hooks/ModalHook";
import { useEffect, useRef, useState } from "react";
import { useAccount } from "@contexts/AccountContext";
import { useSelectedChain } from "@app/examples/ChainSelector";
import { useWallet } from "@providers/wallet.provider";
import { ActionService } from "@api/account.service";

function NavigationBar() {
  const { openModal } = useModal();
  const { address } = useWallet();
  const { refreshAccount } = useAccount();
  const api = new ActionService();
  const { selectedChain: _selectedChain } = useSelectedChain(); // avoid unused var

  const [open, setOpen] = useState(false);
  const btnRef = useRef<HTMLButtonElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);

  // Tutup saat klik di luar + ESC
  useEffect(() => {
    const onDocClick = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (!btnRef.current || !sidebarRef.current) return;
      if (
        !btnRef.current.contains(target) &&
        !sidebarRef.current.contains(target)
      ) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("touchstart", onDocClick, { passive: true });
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("touchstart", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  // Lock scroll ketika sidebar open
  useEffect(() => {
    if (open) {
      const original = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [open]);

  const handleWalletStatus = async () => {
    if (address) {
      const reg = await api.checkRegis(address);
      if (reg) {
        try {
          const me = await api.get();
          if (me.walletAddress !== address) localStorage.removeItem("token");
        } catch {
          localStorage.removeItem("token");
        }
        refreshAccount();
      }
    }
  };

  useEffect(() => {
    if (address) handleWalletStatus();
    else localStorage.removeItem("token");
  }, [address]);

  return (
    <>
      <header className="js-page-header fixed top-0 z-50 mx-auto w-full backdrop-blur transition-colors">
        <div className="mx-6 flex items-center border-b py-4 xl:px-24">
          <div className="shrink-0">
            <Link to="/" className="shrink-0">
              <img
                style={{ height: "48px" }}
                src="/src/img/logo/logo.png"
                className="dark:block"
                alt="BIG BANG X"
              />
            </Link>
          </div>

          {/* Desktop nav */}
          <div className="z-10 hidden w-full items-center bg-white opacity-0 dark:bg-jacarta-800 lg:inset-auto lg:flex lg:justify-start lg:bg-transparent lg:opacity-100 dark:lg:bg-transparent">
            <div className="t-0 z-10 flex items-center bg-white p-6 dark:bg-jacarta-800 lg:hidden">
              <a href="/" className="shrink-0">
                <img
                  style={{ height: "48px" }}
                  src="/src/img/logo/logo.png"
                  className="hidden max-h-7 dark:block"
                  alt="BIG BANG X"
                />
              </a>
              <button
                className="js-mobile-close group ml-2 flex h-10 w-10 items-center justify-center rounded-full border border-jacarta-100 bg-white transition-colors hover:border-transparent hover:bg-accent focus:border-transparent focus:bg-accent dark:border-transparent dark:bg-white/[.15] dark:hover:bg-accent"
                aria-label="close mobile menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  width="24"
                  height="24"
                  className="h-4 w-4 fill-jacarta-700 transition-colors group-hover:fill-white group-focus:fill-white dark:fill-white"
                >
                  <path fill="none" d="M0 0h24v24H0z" />
                  <path d="M12 10.586l4.95-4.95 1.414 1.414-4.95 4.95 4.95 4.95-1.414 1.414-4.95-4.95-4.95 4.95-1.414-1.414 4.95-4.95-4.95-4.95L7.05 5.636z" />
                </svg>
              </button>
            </div>

            <nav className="flex w-full items-center justify-between">
              <ul className="flex flex-col lg:flex-row">
                <li className="group">
                  <Link
                    to="/"
                    className="flex items-center justify-between py-3.5 font-display text-sm text-jacarta-700 hover:text-accent focus:text-accent dark:text-white dark:hover:text-jacarta-400 dark:focus:text-jacarta-400 lg:px-5"
                  >
                    Home
                  </Link>
                </li>

                <li className="group">
                  <Link
                    to="/collections"
                    className="flex items-center justify-between py-3.5 font-display text-sm text-jacarta-700 hover:text-accent focus:text-accent dark:text-white dark:hover:text-jacarta-400 dark:focus:text-jacarta-400 lg:px-5"
                  >
                    Collections
                  </Link>
                </li>

                {/* Dropdown "Create" (desktop tetap) */}
                <li className="js-nav-dropdown group relative">
                  <a
                    href="#"
                    className="dropdown-toggle flex items-center justify-between py-3.5 font-display text-sm text-jacarta-700 hover:text-accent focus:text-accent dark:text-white dark:hover:text-jacarta-400 dark:focus:text-accent lg:px-5"
                    id="navDropdown-create"
                    aria-expanded="false"
                    role="button"
                    data-bs-toggle="dropdown"
                  >
                    Create
                    <i className="lg:hidden">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        className="h-4 w-4 dark:fill-white"
                      >
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z" />
                      </svg>
                    </i>
                  </a>
                  <ul
                    className="dropdown-menu group-hover:visible lg:invisible left-0 top-[85%] z-10 hidden min-w-[200px] gap-x-4 whitespace-nowrap rounded-xl bg-white transition-all will-change-transform group-hover:opacity-100 dark:bg-gray-900 lg:absolute lg:grid lg:translate-y-4 lg:px-2 lg:py-4 lg:opacity-0 lg:shadow-2xl lg:group-hover:translate-y-2"
                    aria-labelledby="navDropdown-create"
                  >
                    <li>
                      <Link
                        to="/launchpad"
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        Launchpad
                      </Link>
                    </li>
                    <li>
                      <Link
                        to={"/collection/create"}
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          New Collection
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/item/create"
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          New Item
                        </span>
                      </Link>
                    </li>
                  </ul>
                </li>

                {/* Dropdown "More" (desktop tetap) */}
                <li className="js-nav-dropdown group relative">
                  <a
                    href="#"
                    className="dropdown-toggle flex items-center justify-between py-3.5 font-display text-sm text-jacarta-700 hover:text-accent focus:text-accent dark:text-white dark:hover:text-jacarta-400 dark:focus:text-accent lg:px-5"
                    id="navDropdown-4"
                    aria-expanded="false"
                    role="button"
                    data-bs-toggle="dropdown"
                  >
                    More
                    <i className="lg:hidden">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        className="h-4 w-4 dark:fill-white"
                      >
                        <path fill="none" d="M0 0h24v24H0z" />
                        <path d="M12 13.172l4.95-4.95 1.414 1.414L12 16 5.636 9.636 7.05 8.222z" />
                      </svg>
                    </i>
                  </a>
                  <ul
                    className="dropdown-menu group-hover:visible lg:invisible left-0 top-[85%] z-10 hidden min-w-[200px] gap-x-4 whitespace-nowrap rounded-xl bg-white transition-all will-change-transform group-hover:opacity-100 dark:bg-gray-900 lg:absolute lg:grid lg:translate-y-4 lg:px-2 lg:py-4 lg:opacity-0 lg:shadow-2xl lg:group-hover:translate-y-2"
                    aria-labelledby="navDropdown-4"
                  >
                    <li>
                      <Link
                        to={"https://old.bigbangx.io/"}
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          Old Bigbangx
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="https://garuda-defi.org/nft-staking/"
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          Stake NFTs
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="/tokens"
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          Register Token
                        </span>
                      </Link>
                    </li>
                    <li>
                      <Link
                        to="https://garudauniverse.gitbook.io/bigbangx"
                        className="flex items-center rounded-xl px-5 py-2 transition-colors hover:bg-jacarta-50 hover:text-accent focus:text-accent dark:hover:bg-jacarta-500"
                      >
                        <span className="font-display text-sm text-jacarta-700 dark:text-white">
                          Documentation
                        </span>
                      </Link>
                    </li>
                  </ul>
                </li>
              </ul>

              {/* Desktop right: connect / profile */}
              <div className="hidden lg:flex xl:ml-12">
                <button
                  className={`group flex items-center justify-center rounded-md border-jacarta-100 bg-white px-4 py-2 transition-colors hover:border-transparent hover:bg-accent focus:border-transparent focus:bg-accent dark:border-transparent dark:bg-white/[.15] dark:hover:bg-accent ${
                    address ? "hidden" : ""
                  }`}
                  aria-label="wallet"
                  onClick={() => openModal("wallet")}
                >
                  <p>Connect Wallet</p>
                </button>
                {address && <ProfileDropdown />}
              </div>
            </nav>

            {/* Section ini memang untuk mobile lama; biarkan hidden di lg */}
            <div className="mt-10 w-full lg:hidden">
              <button
                className="js-wallet block w-full rounded-full bg-accent px-8 py-3 text-center font-semibold text-white shadow-accent-volume transition-all hover:bg-accent-dark"
                onClick={() => openModal("wallet")}
              >
                Connect Wallet
              </button>

              <hr className="my-5 h-px border-0 bg-jacarta-100 dark:bg-jacarta-600" />

              <div className="flex items-center justify-center space-x-5">
                {/* socials placeholder */}
                <a href="#" className="group">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="facebook"
                    className="h-5 w-5 fill-jacarta-300 group-hover:fill-accent dark:group-hover:fill-white"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.78 90.69 226.38 209.25 245V327.69h-63V256h63v-54.64c0-62.15 37-96.48 93.67-96.48 27.14 0 55.52 4.84 55.52 4.84v61h-31.28c-30.8 0-40.41 19.12-40.41 38.73V256h68.78l-11 71.69h-57.78V501C413.31 482.38 504 379.78 504 256z"></path>
                  </svg>
                </a>
                <a href="#" className="group">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="twitter"
                    className="h-5 w-5 fill-jacarta-300 group-hover:fill-accent dark:group-hover:fill-white"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 512 512"
                  >
                    <path d="M459.37 151.716c.325 4.548.325 9.097.325 13.645 0 138.72-105.583 298.558-298.558 298.558-59.452 0-114.68-17.219-161.137-47.106 8.447.974 16.568 1.299 25.34 1.299 49.055 0 94.213-16.568 130.274-44.832-46.132-.975-84.792-31.188-98.112-72.772 6.498.974 12.995 1.624 19.818 1.624 9.421 0 18.843-1.3 27.614-3.573-48.081-9.747-84.143-51.98-84.143-102.985v-1.299c13.969 7.797 30.214 12.67 47.431 13.319-28.264-18.843-46.781-51.005-46.781-87.391 0-19.492 5.197-37.36 14.294-52.954 51.655 63.675 129.3 105.258 216.365 109.807-1.624-7.797-2.599-15.918-2.599-24.04 0-57.828 46.782-104.934 104.934-104.934 30.213 0 57.502 12.67 76.67 33.137 23.715-4.548 46.456-13.32 66.599-25.34-7.798 24.366-24.366 44.833-46.132 57.827 21.117-2.273 41.584-8.122 60.426-16.243-14.292 20.791-32.161 39.308-52.628 54.253z"></path>
                  </svg>
                </a>
                <a href="#" className="group">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="discord"
                    className="h-5 w-5 fill-jacarta-300 group-hover:fill-accent dark:group-hover:fill-white"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 640 512"
                  >
                    <path d="M524.531,69.836a1.5,1.5,0,0,0-.764-.7A485.065,485.065,0,0,0,404.081,32.03a1.816,1.816,0,0,0-1.923.91,337.461,337.461,0,0,0-14.9,30.6,447.848,447.848,0,0,0-134.426,0,309.541,309.541,0,0,0-15.135-30.6,1.89,1.89,0,0,0-1.924-.91A483.689,483.689,0,0,0,116.085,69.137a1.712,1.712,0,0,0-.788.676C39.068,183.651,18.186,294.69,28.43,404.354a2.016,2.016,0,0,0,.765,1.375A487.666,487.666,0,0,0,176.02,479.918a1.9,1.9,0,0,0,2.063-.676A348.2,348.2,0,0,0,208.12,430.4a1.86,1.86,0,0,0-1.019-2.588,321.173,321.173,0,0,1-45.868-21.853,1.885,1.885,0,0,1-.185-3.126c3.082-2.309,6.166-4.711,9.109-7.137a1.819,1.819,0,0,1,1.9-.256c96.229,43.917,200.41,43.917,295.5,0a1.812,1.812,0,0,1,1.924.233c2.944,2.426,6.027,4.851,9.132,7.16a1.884,1.884,0,0,1-.162,3.126,301.407,301.407,0,0,1-45.89,21.83,1.875,1.875,0,0,0-1,2.611,391.055,391.055,0,0,0,30.014,48.815,1.864,1.864,0,0,0,2.063.7A486.048,486.048,0,0,0,610.7,405.729a1.882,1.882,0,0,0,.765-1.352C623.729,277.594,590.933,167.465,524.531,69.836ZM222.491,337.58c-28.972,0-52.844-26.587-52.844-59.239S193.056,219.1,222.491,219.1c29.665,0,53.306,26.82,52.843,59.239C275.334,310.993,251.924,337.58,222.491,337.58Zm195.38,0c-28.971,0-52.843-26.587-52.843-59.239S388.437,219.1,417.871,219.1c29.667,0,53.307,26.82,52.844,59.239C470.715,310.993,447.538,337.58,417.871,337.58Z"></path>
                  </svg>
                </a>
                <a href="#" className="group">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="instagram"
                    className="h-5 w-5 fill-jacarta-300 group-hover:fill-accent dark:group-hover:fill-white"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                  >
                    <path d="M224.1 141c-63.6 0-114.9 51.3-114.9 114.9s51.3 114.9 114.9 114.9S339 319.5 339 255.9 287.7 141 224.1 141zm0 189.6c-41.1 0-74.7-33.5-74.7-74.7s33.5-74.7 74.7-74.7 74.7 33.5 74.7 74.7-33.6 74.7-74.7 74.7zm146.4-194.3c0 14.9-12 26.8-26.8 26.8-14.9 0-26.8-12-26.8-26.8s12-26.8 26.8-26.8 26.8 12 26.8 26.8zm76.1 27.2c-1.7-35.9-9.9-67.7-36.2-93.9-26.2-26.2-58-34.4-93.9-36.2-37-2.1-147.9-2.1-184.9 0-35.8 1.7-67.6 9.9-93.9 36.1s-34.4 58-36.2 93.9c-2.1 37-2.1 147.9 0 184.9 1.7 35.9 9.9 67.7 36.2 93.9s58 34.4 93.9 36.2c37 2.1 147.9 2.1 184.9 0 35.9-1.7 67.7-9.9 93.9-36.2 26.2-26.2 34.4-58 36.2-93.9 2.1-37 2.1-147.8 0-184.8zM398.8 388c-7.8 19.6-22.9 34.7-42.6 42.6-29.5 11.7-99.5 9-132.1 9s-102.7 2.6-132.1-9c-19.6-7.8-34.7-22.9-42.6-42.6-11.7-29.5-9-99.5-9-132.1s-2.6-102.7 9-132.1c7.8-19.6 22.9-34.7 42.6-42.6 29.5-11.7 99.5-9 132.1-9s102.7-2.6 132.1 9c19.6 7.8 34.7 22.9 42.6 42.6 11.7 29.5 9 99.5 9 132.1s2.7 102.7-9 132.1z"></path>
                  </svg>
                </a>
                <a href="#" className="group">
                  <svg
                    aria-hidden="true"
                    focusable="false"
                    data-prefix="fab"
                    data-icon="tiktok"
                    className="h-5 w-5 fill-jacarta-300 group-hover:fill-accent dark:group-hover:fill-white"
                    role="img"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 448 512"
                  >
                    <path d="M448,209.91a210.06,210.06,0,0,1-122.77-39.25V349.38A162.55,162.55,0,1,1,185,188.31V278.2a74.62,74.62,0,1,0,52.23,71.18V0l88,0a121.18,121.18,0,0,0,1.86,22.17h0A122.18,122.18,0,0,0,381,102.39a121.43,121.43,0,0,0,67,20.14Z"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>

          {/* Right (mobile): wallet + profile + hamburger */}
          <div className="ml-auto flex lg:hidden">
            <button
              className={`group flex h-10 w-10 items-center justify-center rounded-full border border-jacarta-100 bg-white transition-colors hover:border-transparent hover:bg-accent focus:border-transparent focus:bg-accent dark:border-transparent dark:bg-white/[.15] dark:hover:bg-accent ${
                address ? "hidden" : ""
              }`}
              aria-label="wallet"
              onClick={() => openModal("wallet")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="24"
                height="24"
                className="h-4 w-4 fill-jacarta-700 transition-colors group-hover:fill-white group-focus:fill-white dark:fill-white"
              >
                <path fill="none" d="M0 0h24v24H0z" />
                <path d="M22 6h-7a6 6 0 1 0 0 12h7v2a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h18a1 1 0 0 1 1 1v2zm-7 2h8v8h-8a4 4 0 1 1 0-8zm0 3v2h3v-2h-3z" />
              </svg>
            </button>
            {address && <ProfileDropdown />}

            {/* Trigger + Overlay + Sidebar */}
            <div className="relative">
              {/* Trigger: Hamburger */}
              <button
                ref={btnRef}
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="group ml-2 flex h-10 w-10 items-center justify-center rounded-full border border-jacarta-100 bg-white transition-colors hover:border-transparent hover:bg-accent focus:border-transparent focus:bg-accent dark:border-transparent dark:bg-white/[.15] dark:hover:bg-accent"
                aria-expanded={open}
                aria-controls="mobile-sidebar"
                aria-label="Open menu"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  className="h-4 w-4 fill-jacarta-700 transition-colors group-hover:fill-white group-focus:fill-white dark:fill-white"
                >
                  <path d="M3 6h18v2H3V6zm0 5h18v2H3v-2zm0 5h18v2H3v-2z" />
                </svg>
              </button>

              {/* Overlay */}
              <div
                className={`${
                  open
                    ? "bg-black/40 fixed inset-0 z-40 backdrop-blur-[1px]"
                    : "hidden"
                } lg:hidden`}
                onClick={() => setOpen(false)}
              />

              {/* Sidebar (Off-canvas) */}
              <aside
                ref={sidebarRef}
                id="mobile-sidebar"
                role="dialog"
                aria-modal="true"
                className={`fixed right-0 top-0 z-50 h-screen w-[85%] max-w-sm transform bg-white p-6 shadow-2xl transition-transform duration-300 dark:bg-jacarta-900 ${
                  open ? "translate-x-0" : "translate-x-full"
                } lg:hidden`}
              >
                {/* Header sidebar */}
                <div className="mb-6 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src="/src/img/logo/logo.png"
                      alt="BIG BANG X"
                      className="h-8"
                    />
                    <span className="font-display text-base text-jacarta-700 dark:text-white">
                      Menu
                    </span>
                  </div>
                  <button
                    onClick={() => setOpen(false)}
                    aria-label="Close menu"
                    className="flex h-9 w-9 items-center justify-center rounded-full hover:bg-jacarta-50 dark:hover:bg-jacarta-700"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      className="fill-jacarta-700 dark:fill-white"
                    >
                      <path d="M18.3 5.71L12 12.01l-6.3-6.3-1.4 1.41 6.29 6.29-6.29 6.3 1.41 1.41 6.29-6.29 6.3 6.29 1.41-1.41-6.29-6.3 6.29-6.29z" />
                    </svg>
                  </button>
                </div>

                {/* Isi menu */}
                <nav className="space-y-1">
                  <Link
                    to="/"
                    onClick={() => setOpen(false)}
                    className="block rounded-lg px-4 py-3 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                  >
                    Home
                  </Link>
                  <Link
                    to="/collections"
                    onClick={() => setOpen(false)}
                    className="block rounded-lg px-4 py-3 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                  >
                    Collections
                  </Link>

                  <div className="mt-3 rounded-xl border border-jacarta-100 p-2 dark:border-jacarta-700">
                    <p className="px-2 pb-1 text-xs uppercase tracking-wide text-jacarta-400 dark:text-jacarta-300">
                      Create
                    </p>
                    <Link
                      to="/launchpad"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-3 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      Launchpad
                    </Link>
                    <Link
                      to="/collection/create"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      New Collection
                    </Link>
                    <Link
                      to="/item/create"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      New NFT
                    </Link>
                  </div>

                  <div className="mt-3 rounded-xl border border-jacarta-100 p-2 dark:border-jacarta-700">
                    <p className="px-2 pb-1 text-xs uppercase tracking-wide text-jacarta-400 dark:text-jacarta-300">
                      More
                    </p>
                    <Link
                      to="https://old.bigbangx.io/"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      Old Bigbangx
                    </Link>
                    <Link
                      to="https://garuda-defi.org/nft-staking/"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      Stake NFTs
                    </Link>
                    <Link
                      to="/tokens"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      Register Token
                    </Link>
                    <Link
                      to="https://garudauniverse.gitbook.io/bigbangx"
                      onClick={() => setOpen(false)}
                      className="block rounded-lg px-4 py-2 font-display text-sm text-jacarta-700 hover:bg-jacarta-50 hover:text-accent dark:text-white dark:hover:bg-jacarta-700"
                    >
                      Documentation
                    </Link>
                  </div>

                  {!address && (
                    <button
                      className="mt-4 w-full rounded-full bg-accent px-5 py-3 text-center font-semibold text-white shadow-accent-volume transition-all hover:bg-accent-dark"
                      onClick={() => {
                        setOpen(false);
                        openModal("wallet");
                      }}
                    >
                      Connect Wallet
                    </button>
                  )}
                </nav>

                {/* Socials (opsional) */}
                {/* <div className="mt-6 flex items-center gap-4">
                  <a
                    href="#"
                    aria-label="Twitter"
                    className="rounded p-2 hover:bg-jacarta-50 dark:hover:bg-jacarta-700"
                  >
                    <svg viewBox="0 0 24 24" width="18" height="18" className="fill-jacarta-700 dark:fill-white">
                      <path d="M22 5.7c-.7.3-1.4.5-2.2.6.8-.5 1.3-1.1 1.6-2-.7.4-1.6.8-2.4 1-1.5-1.6-4.3-.8-4.6 1.4-.1.6 0 1.1.2 1.6-3.2-.2-6-1.7-7.9-4.3-1 1.7-.5 3.9 1.2 5-.6 0-1.2-.2-1.7-.5 0 1.9 1.3 3.4 3.1 3.8-.5.1-1 .2-1.5.1.4 1.5 1.9 2.6 3.6 2.7-1.4 1.1-3.1 1.7-4.9 1.7H3c1.8 1.1 3.9 1.7 6.1 1.7 7.4 0 11.6-6.3 11.3-11.9.8-.6 1.4-1.2 1.6-1.9z"/>
                    </svg>
                  </a>
                </div> */}
              </aside>
            </div>
          </div>
        </div>
      </header>
    </>
  );
}

export default NavigationBar;
